import { Check, UserRoundPlus } from 'lucide-react';
import { Fragment, useState } from 'react';

import { buildActivityAssigneeOptions } from '@apps/web/components/home/activity_assignment';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';
import { useActivityEventAssignees } from '@apps/web/swr_data_fetching/activity_event_assignments';

type ConversationAssigneePickerProps = {
    readonly organizationId: string;
    readonly conversationId: string;
    readonly assignedToUserId: string | null;
    readonly assignedToUserName: string | null;
    readonly onAssign: (assignedToUserId: string | null) => void;
    readonly triggerAriaLabel?: string;
    readonly triggerClassName?: string;
};

const ASSIGNEE_AVATAR_COLORS = [
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-800',
    'bg-violet-100 text-violet-700',
    'bg-rose-100 text-rose-700',
    'bg-cyan-100 text-cyan-700',
    'bg-orange-100 text-orange-700',
    'bg-indigo-100 text-indigo-700',
] as const;

// Deterministic avatar color keyed off the assignee id so a person keeps the
// same swatch across rows, mirroring the activity feed assignment UI.
function assigneeAvatarColor(key: string): string {
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
        hash = (hash * 31 + key.charCodeAt(index)) | 0;
    }
    const paletteIndex = Math.abs(hash) % ASSIGNEE_AVATAR_COLORS.length;
    return ASSIGNEE_AVATAR_COLORS[paletteIndex] ?? ASSIGNEE_AVATAR_COLORS[0];
}

export function assigneeInitials(name: string): string {
    const displayName = name.split(' (', 1)[0] ?? name;
    const parts = displayName.split(/\s+/).filter((part) => part.length > 0);
    if (parts.length === 0) {
        return '?';
    }
    return parts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

// Small colored-initials avatar for an assignee. Rendered on list rows and
// board cards. `name` null => nobody assigned (caller decides whether to show).
export function ConversationAssigneeAvatar({
    userId,
    name,
    className,
}: {
    readonly userId: string;
    readonly name: string;
    readonly className?: string;
}) {
    return (
        <span
            className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold',
                assigneeAvatarColor(userId),
                className,
            )}
            title={name}
        >
            {assigneeInitials(name)}
        </span>
    );
}

// Assignee picker for a single conversation. Fetches the org's assignable
// members and lets the operator assign, reassign, or unassign. Reuses the
// activity-events assignees endpoint per the design contract.
export function ConversationAssigneePicker({
    organizationId,
    conversationId: _conversationId,
    assignedToUserId,
    assignedToUserName,
    onAssign,
    triggerAriaLabel,
    triggerClassName,
}: ConversationAssigneePickerProps) {
    const [open, setOpen] = useState(false);
    const { data: assigneesData } = useActivityEventAssignees(organizationId, [
        organizationId,
    ]);
    const assigneeOptions = buildActivityAssigneeOptions(
        assigneesData?.users ?? [],
    );

    const assignLabel = assignedToUserName
        ? `Assigned to ${assignedToUserName}`
        : 'Assign conversation';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {/* The tooltip wraps the trigger rather than the picker: this
                component forwards no ref, so a TooltipTrigger placed outside
                it would have nothing to attach to and never open. */}
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <PopoverTrigger asChild={true}>
                        <Button
                            aria-label={triggerAriaLabel ?? assignLabel}
                            className={cn(
                                'h-11 w-11 md:h-8 md:w-8',
                                triggerClassName,
                            )}
                            size="icon"
                            type="button"
                            variant="ghost"
                        >
                            {assignedToUserId && assignedToUserName ? (
                                <ConversationAssigneeAvatar
                                    userId={assignedToUserId}
                                    name={assignedToUserName}
                                />
                            ) : (
                                <UserRoundPlus className="h-4 w-4" />
                            )}
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                    {triggerAriaLabel ?? assignLabel}
                </TooltipContent>
            </Tooltip>
            <PopoverContent align="end" className="w-64 p-0">
                <Command>
                    <CommandInput placeholder="Assign to…" className="h-9" />
                    <CommandList className="max-h-56">
                        <CommandEmpty>No people found.</CommandEmpty>
                        {assignedToUserId ? (
                            <Fragment>
                                <CommandGroup>
                                    <CommandItem
                                        value="unassign"
                                        onSelect={() => {
                                            onAssign(null);
                                            setOpen(false);
                                        }}
                                    >
                                        Unassign
                                    </CommandItem>
                                </CommandGroup>
                                <CommandSeparator />
                            </Fragment>
                        ) : null}
                        <CommandGroup>
                            {assigneeOptions.map((option) => (
                                <CommandItem
                                    key={option.id}
                                    value={option.label}
                                    disabled={option.id === assignedToUserId}
                                    onSelect={() => {
                                        onAssign(option.id);
                                        setOpen(false);
                                    }}
                                >
                                    <span className="truncate">
                                        {option.label}
                                    </span>
                                    {option.id === assignedToUserId ? (
                                        <Check className="ml-auto h-3.5 w-3.5" />
                                    ) : null}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
